import { lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';
import PrivateRoute from 'components/route/private';
import PrivateAdminRoute from 'components/route/private_admin';
import PublicAuthRoute from 'components/route/public_auth';
import PrivateNoSpaceRoute from 'components/route/private_no_space';
import SpaceRedirect from 'components/route/space_redirect';
import RootLayout from './root_layout';

const Dashboard = lazy(() => import('pages/dashboard'));
const UserLogin = lazy(() => import('pages/user/login'));
const UserRegister = lazy(() => import('pages/user/register'));
const UserForgotPassword = lazy(() => import('pages/user/forgot_password'));
const UserConfirmation = lazy(() => import('pages/user/confirmation'));
const UserResetPassword = lazy(() => import('pages/user/reset_password'));
const Profile = lazy(() => import('pages/user/profile'));
const DeleteConfirmation = lazy(() => import('pages/user/delete_confirmation'));
const Inspections = lazy(() => import('pages/inspections'));
const InspectionsCalendar = lazy(() => import('pages/inspections/calendar'));
const InspectionSubjects = lazy(() => import('pages/inspection_subjects'));
const Places = lazy(() => import('pages/places'));
const PlacesShow = lazy(() => import('pages/places/show'));
const InspectionSubject = lazy(() => import('pages/inspection_subjects/show'));
const Inspection = lazy(() => import('pages/inspections/show'));
const InspectionResult = lazy(() => import('pages/inspection_results/show'));
const Templates = lazy(() => import('pages/templates'));
const Template = lazy(() => import('pages/templates/show'));
const Spaces = lazy(() => import('pages/spaces'));
const SpaceShow = lazy(() => import('pages/spaces/show'));
const SpaceUsersIndex = lazy(() => import('pages/spaces/users'));
const SpacePayments = lazy(() => import('pages/spaces/billing'));
const PaymentRedirect = lazy(() => import('pages/spaces/billing/payment_redirect'));
const Notifications = lazy(() => import('pages/notifications'));
const NotFound = lazy(() => import('pages/not_found'));
const SomethingWrong = lazy(() => import('pages/something_wrong'));

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route element={<PublicAuthRoute />}>
        <Route path="/users/login" element={<UserLogin />} />
        <Route path="/users/register" element={<UserRegister />} />
        <Route path="/users/forgot_password" element={<UserForgotPassword />} />
        <Route path="/users/reset_password/:token" element={<UserResetPassword />} />
        <Route path="/users/confirm/:token" element={<UserConfirmation />} />
        <Route path="*" element={<UserLogin />} />
      </Route>
      <Route element={<PrivateNoSpaceRoute />}>
        <Route path="/spaces" element={<Spaces />} />
        <Route path="/spaces/:spaceId" element={<SpaceRedirect to="/dashboard" />} />
        <Route path="/spaces/:spaceId/inspections" element={<SpaceRedirect to="/inspections" />} />
        <Route path="/spaces/:spaceId/inspections/:inspectionId" element={<SpaceRedirect to="/inspections/:inspectionId" />} />
        <Route path="/not-found" element={<NotFound />} />
        <Route path="/something-wrong" element={<SomethingWrong />} />
      </Route>
      <Route element={<PrivateRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/inspections" element={<Inspections />} />
        <Route path="/inspections/calendar" element={<InspectionsCalendar />} />
        <Route path="/inspections/:inspectionId" element={<Inspection />} />
        <Route
          path="/inspection_results/:inspectionResultId"
          element={<InspectionResult />}
        />
        <Route path="/inspection_subjects" element={<InspectionSubjects />} />
        <Route path="/inspection_subjects/:inspectionSubjectId" element={<InspectionSubject />} />

        <Route path="/places" element={<Places />} />
        <Route path="/places/:id" element={<PlacesShow />} />

        <Route path="/users/delete/:token" element={<DeleteConfirmation />} />

        <Route path="/templates" element={<Templates />} />
        <Route path="/templates/:id" element={<Template />} />

        <Route element={<PrivateAdminRoute />}>
          <Route path="/space/billing/success" element={<PaymentRedirect outcome="success" />} />
          <Route path="/space/billing/failure" element={<PaymentRedirect outcome="failure" />} />
          <Route path="/space/billing" element={<SpacePayments />} />
          <Route path="/space/show" element={<SpaceShow />} />
          <Route path="/space/users" element={<SpaceUsersIndex />} />
        </Route>

        <Route path="/notifications" element={<Notifications />} />

        <Route path="*" element={<Dashboard />} />
      </Route>
    </Route>
  )
);

export default router;
